import { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  LayoutDashboard,
  Users,
  BedDouble,
  CreditCard,
  CalendarCheck,
  Settings,
  LogOut,
  Building2,
  ChevronDown,
  Menu,
  X,
  Wallet,
  Ban,
  Home,
  Banknote,
  Search,
  User,
  DoorOpen,
  Phone,
  Mail,
  Loader2,
} from "lucide-react";
import api from "../lib/api";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/guests", label: "Bewohner", icon: Users },
  { path: "/rooms", label: "Zimmer", icon: BedDouble },
  { path: "/wohnungen", label: "Wohnungen", icon: Home },
  { path: "/bookings", label: "Aufnahme", icon: CalendarCheck },
  { path: "/payments", label: "Zahlungen", icon: CreditCard },
  { path: "/accounts", label: "Konten", icon: Wallet },
  { path: "/kasse", label: "Kasse", icon: Banknote },
  { path: "/bankumsaetze", label: "Bankumsätze", icon: Building2 },
  { path: "/blacklist", label: "Blacklist", icon: Ban },
  { path: "/settings", label: "Einstellungen", icon: Settings },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    const searchTimer = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setIsSearching(true);
        try {
          const [guestsRes, bookingsRes] = await Promise.all([
            api.get("/guests"),
            api.get("/bookings"),
          ]);
          
          const query = searchQuery.toLowerCase();
          const matchedGuests = guestsRes.data.filter(g => 
            `${g.first_name} ${g.last_name}`.toLowerCase().includes(query) ||
            g.bg_number?.toLowerCase().includes(query)
          );
          
          // Enrich with booking info
          const enrichedResults = matchedGuests.map(guest => {
            const activeBooking = bookingsRes.data.find(b => 
              b.guest_id === guest.id && b.status === 'active'
            );
            return {
              ...guest,
              activeBooking,
              roomNumber: activeBooking?.room_number,
              bedNumber: activeBooking?.bed_number,
            };
          });
          
          setSearchResults(enrichedResults.slice(0, 5));
          setShowResults(true);
        } catch (error) {
          console.error("Search error:", error);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(searchTimer);
  }, [searchQuery]);

  const handleResultClick = (guest) => {
    setShowResults(false);
    setSearchQuery("");
    navigate(`/guests?id=${guest.id}`);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: "Administrator",
      manager: "Manager",
      staff: "Mitarbeiter",
    };
    return badges[role] || role;
  };

  const NavContent = ({ mobile = false }) => (
    <>
      {navItems.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.path === "/"}
          onClick={() => mobile && setMobileMenuOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
              isActive
                ? "text-white bg-slate-700"
                : "text-slate-400 hover:text-white hover:bg-slate-700/50"
            }`
          }
          data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
        >
          <item.icon className="h-5 w-5" strokeWidth={1.5} />
          <span className="text-sm font-medium">{item.label}</span>
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-slate-50" data-testid="app-layout">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 text-white px-4 py-3 flex items-center justify-between" style={{ backgroundColor: '#2e3436' }}>
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_jobcenterhome/artifacts/evl5y4pc_ditib_logo_DE_2026_Wohneim_white.png" 
            alt="Logo" 
            className="h-8 max-w-[120px] object-contain" 
          />
        </div>
        <div className="flex items-center gap-2">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="text-white hover:bg-slate-700">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
          <SheetContent side="left" className="w-72 text-slate-300 p-0 border-slate-700" style={{ backgroundColor: '#2e3436' }}>
            {/* Mobile Menu Header */}
            <div className="p-4 border-b border-slate-700">
              <div className="flex flex-col items-center text-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_jobcenterhome/artifacts/evl5y4pc_ditib_logo_DE_2026_Wohneim_white.png" 
                  alt="Logo" 
                  className="h-12 max-w-[180px] object-contain" 
                />
                <p className="text-sm font-medium text-white -mt-1">Verwaltungssystem</p>
              </div>
            </div>

            {/* Mobile Navigation */}
            <nav className="flex-1 p-4 space-y-1">
              <NavContent mobile />
            </nav>

            {/* Mobile User Menu */}
            <div className="p-4 border-t border-slate-700">
              <div className="flex items-center gap-3 mb-4">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="text-white text-sm" style={{ backgroundColor: '#c11718' }}>
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{user?.name}</p>
                  <p className="text-xs text-slate-400">{getRoleBadge(user?.role)}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-slate-700"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Abmelden
              </Button>
            </div>
          </SheetContent>
        </Sheet>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 text-slate-300 flex-col fixed h-screen" style={{ backgroundColor: '#2e3436' }} data-testid="sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex flex-col items-center text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_jobcenterhome/artifacts/evl5y4pc_ditib_logo_DE_2026_Wohneim_white.png" 
              alt="DITIB Nürnberg Wohnheim" 
              className="h-20 max-w-[220px] object-contain" 
            />
            <p className="text-sm font-medium text-white -mt-1">Verwaltungssystem</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          <NavContent />
        </nav>

        {/* User Menu */}
        <div className="p-4 border-t border-slate-700">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700 p-2"
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-8 w-8 mr-3">
                  <AvatarFallback className="text-white text-xs" style={{ backgroundColor: '#c11718' }}>
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400">{getRoleBadge(user?.role)}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/settings")}>
                <Settings className="h-4 w-4 mr-2" />
                Einstellungen
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer text-red-600" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Abmelden
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        {/* Top Search Bar */}
        <div className="sticky top-0 z-40 bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-3 hidden lg:block">
          <div className="flex items-center justify-center">
            <div ref={searchRef} className="relative w-full max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Bewohner suchen (Name oder BG-Nummer)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowResults(true)}
                  className="pl-10 pr-10 h-10 bg-slate-50 border-slate-200 focus:bg-white"
                  data-testid="global-search-input"
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                )}
                {searchQuery && !isSearching && (
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setSearchResults([]);
                      setShowResults(false);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {/* Search Results Dropdown */}
              {showResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden z-50">
                  {searchResults.map((guest) => (
                    <div
                      key={guest.id}
                      onClick={() => handleResultClick(guest)}
                      className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0 transition-colors"
                      data-testid={`search-result-${guest.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-slate-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-slate-900">
                              {guest.first_name} {guest.last_name}
                            </span>
                            {guest.activeBooking ? (
                              <Badge className="bg-emerald-100 text-emerald-700 text-[10px] h-5">
                                Aktiv
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] h-5 text-slate-500">
                                Inaktiv
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            {guest.roomNumber && (
                              <span className="flex items-center gap-1">
                                <DoorOpen className="h-3 w-3" />
                                Zi. {guest.roomNumber}
                                {guest.bedNumber && ` / ${guest.bedNumber}`}
                              </span>
                            )}
                            {guest.bg_number && (
                              <span className="flex items-center gap-1">
                                <CreditCard className="h-3 w-3" />
                                {guest.bg_number}
                              </span>
                            )}
                            {guest.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {guest.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="p-2 bg-slate-50 text-center">
                    <button
                      onClick={() => {
                        setShowResults(false);
                        navigate(`/guests?search=${encodeURIComponent(searchQuery)}`);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Alle Ergebnisse anzeigen →
                    </button>
                  </div>
                </div>
              )}
              
              {/* No results message */}
              {showResults && searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 p-4 text-center z-50">
                  <p className="text-sm text-slate-500">Keine Bewohner gefunden</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
